import type { ButtonProps } from '@openx/components-core/lib/Button/Button.tsx';
import type { UseConfirmationDialogParams } from '@openx/components-core/lib/Dialog/ConfirmationDialog/types.ts';
import { useConfirmationDialog } from '@openx/components-core/lib/Dialog/ConfirmationDialog/useConfirmationDialog.tsx';
import { type ListSegment, type SegmentRowActionType, segmentRowActionType } from '@openx/types/segments.ts';
import { type JSX, useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { getActionLabels } from '../../constants.ts';
import { useSegmentContext } from '../utils/context/SegmentContext.tsx';

interface ExtraButtonOptions {
  extraBtnLabel: string;
  extraButtonColor: ButtonProps['color'];
  onExtraAction: () => void;
  showExtraBtn: boolean;
}

interface GetActionModalParams {
  action: SegmentRowActionType;
  confirmationButtonColor?: ButtonProps['color'];
  confirmationDialog: {
    open: (params: Partial<UseConfirmationDialogParams>) => void;
    close: () => void;
    setLoading: (loading: boolean) => void;
  };
  confirmationLabel: string;
  content: JSX.Element | string;
  extraButtonOptions?: ExtraButtonOptions;
  isBulkMode?: boolean;
  onConfirmed(segments: ListSegment[]): Promise<void>;
  segments: ListSegment[];
  title: string;
}

const actionModal = ({
  confirmationDialog,
  confirmationLabel,
  content,
  extraButtonOptions,
  segments,
  title,
  onConfirmed,
  confirmationButtonColor = 'error',
}: GetActionModalParams) => {
  const { open, close } = confirmationDialog;

  open({
    confirmationButtonColor,
    confirmationLabel,
    content,
    customContent: true,
    hideConfirm: segments.length === 0,
    onConfirmed: async () => {
      try {
        await onConfirmed(segments);
      } finally {
        close();
      }
    },
    showExtraBtn: false,
    title: title,
    ...(extraButtonOptions ?? {}),
  });
};

type CallSegmentsAction<T extends ListSegment> = {
  action: SegmentRowActionType;
  content: JSX.Element | string;
  confirmationDisabled?: boolean;
  isBulkMode?: boolean;
  onExtraAction?: () => void;
  onFinish?: () => void;
  preProcessSegments?: (segments: ListSegment[]) => T[];
  segments: ListSegment[];
  skipModal?: boolean;
};

export const useSegmentsOperations = () => {
  const { t } = useTranslation();
  const { useSegmentsOperation } = useSegmentContext();
  const confirmationDialog = useConfirmationDialog();
  const labels = getActionLabels(t);

  const { archive, rename } = useSegmentsOperation();

  const [loadingAction, setLoadingAction] = useState<SegmentRowActionType | null>(null);

  const setLoading = useCallback(
    (action: SegmentRowActionType | null) => {
      setLoadingAction(action);
      confirmationDialog.setLoading(!!action);
    },
    [confirmationDialog]
  );

  const setConfirmationDisabled = useCallback(
    (disabled: boolean) => confirmationDialog.toogleConfirmationDisabled(disabled),
    [confirmationDialog]
  );

  const actions = useMemo(
    () => ({
      [segmentRowActionType.DELETE]: archive,
      [segmentRowActionType.RENAME]: rename,
    }),
    [archive, rename]
  );

  const callSegmentAction = useCallback(
    <T extends ListSegment>({
      action,
      content,
      segments,
      isBulkMode = false,
      skipModal = false,
      onExtraAction = () => {
        return undefined;
      },
      onFinish,
      preProcessSegments = (segments): T[] => segments as unknown as T[],
    }: CallSegmentsAction<T>) => {
      const onConfirmed = async (segments: ListSegment[]) => {
        try {
          setLoading(action);
          const processedSegments = preProcessSegments(segments);
          await actions[action](processedSegments);
        } catch (e) {
          // biome-ignore lint/suspicious/noConsole: logging error in console
          console.error('error while requesting action', action, e);
        } finally {
          setLoading(null);
          onFinish?.();
        }
      };

      if (skipModal) {
        return onConfirmed(segments);
      }

      const segmentLabel = isBulkMode ? t('Segments') : t('Segment');
      const confirmationButtonColor = action === segmentRowActionType.RENAME ? 'primary' : 'error';
      const extraButtonOptions: ExtraButtonOptions | undefined =
        action === segmentRowActionType.DELETE
          ? {
              extraBtnLabel: t('Refresh'),
              extraButtonColor: 'secondary',
              onExtraAction: onExtraAction,
              showExtraBtn: true,
            }
          : undefined;

      actionModal({
        action,
        confirmationButtonColor,
        confirmationDialog,
        confirmationLabel: labels[action],
        content,
        extraButtonOptions,
        isBulkMode,
        onConfirmed: onConfirmed,
        segments,
        title: t('{action} {segment}', {
          action: labels[action],
          segment: segmentLabel,
        }),
      });
    },
    [t, setLoading, actions, confirmationDialog, labels]
  );

  return { callSegmentAction, loadingAction, setConfirmationDisabled };
};
